import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Loader2, X,
  AlertTriangle, CheckCircle2, Circle, CalendarDays,
} from 'lucide-react';
import { Sidebar, Topbar, CategoryChip, TASK_CATEGORY_ORDER, NewTaskDialog } from './Dashboard';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { auth, tasks, clients, team, getStoredUser } from '../api';

const WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// key for a Date -> "YYYY-MM-DD" in local timezone
function dayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildMonthGrid(monthDate) {
  // Returns 42 cells (6 rows) starting from Sunday of the first week
  const first = startOfMonth(monthDate);
  const startWeekday = first.getDay(); // 0=Sun
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

const CATEGORY_DOT = {
  'VAT': 'bg-emerald-500',
  'PAYE': 'bg-blue-500',
  'CIPC': 'bg-purple-500',
  'Beneficial Ownership': 'bg-fuchsia-500',
  'Taxation': 'bg-amber-500',
  'Provisional Tax': 'bg-orange-500',
  'Bookkeeping': 'bg-teal-500',
  'Audit': 'bg-red-500',
  'Advisory': 'bg-indigo-500',
  'Other': 'bg-stone-400',
};

const CATEGORY_BAR = {
  'VAT': 'bg-emerald-100 text-emerald-900 border-l-emerald-500',
  'PAYE': 'bg-blue-100 text-blue-900 border-l-blue-500',
  'CIPC': 'bg-purple-100 text-purple-900 border-l-purple-500',
  'Beneficial Ownership': 'bg-fuchsia-100 text-fuchsia-900 border-l-fuchsia-500',
  'Taxation': 'bg-amber-100 text-amber-900 border-l-amber-500',
  'Provisional Tax': 'bg-orange-100 text-orange-900 border-l-orange-500',
  'Bookkeeping': 'bg-teal-100 text-teal-900 border-l-teal-500',
  'Audit': 'bg-red-100 text-red-900 border-l-red-500',
  'Advisory': 'bg-indigo-100 text-indigo-900 border-l-indigo-500',
  'Other': 'bg-stone-100 text-stone-800 border-l-stone-400',
};

export default function CalendarPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [taskList, setTaskList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [cursor, setCursor] = useState(startOfMonth(new Date())); // first day of visible month
  const [selectedDay, setSelectedDay] = useState(null); // Date object or null
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [dialogDefaultDate, setDialogDefaultDate] = useState('');
  // filters
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [assigneeFilter, setAssigneeFilter] = useState('All');
  const [clientFilter, setClientFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All'); // Open | Done | Overdue | All

  const fetchAll = async () => {
    try {
      const [me, tl, cl, tm] = await Promise.all([
        auth.me().catch(() => null),
        tasks.list(),
        clients.list(),
        team.list().catch(() => ({ members: [] })),
      ]);
      if (me) setUser(me);
      setTaskList(tl);
      setClientList(cl);
      setTeamMembers(tm.members || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const changeStatus = async (t, status) => {
    try {
      const payload = {
        title: t.title, clientId: t.clientId, clientName: t.clientName,
        assigneeId: t.assigneeId, status, dueDate: t.dueDate,
        hours: t.hours || 0, category: t.category,
      };
      await tasks.update(t.id, payload);
      toast({ title: 'Task updated', description: `"${t.title}" → ${status}` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const grid = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const todayKey = dayKey(new Date());

  // Apply filters first, then bucket by day
  const filtered = useMemo(() => {
    return taskList.filter((t) => {
      if (!t.dueDate) return false;
      if (categoryFilter !== 'All' && (t.category || 'Other') !== categoryFilter) return false;
      if (assigneeFilter !== 'All' && t.assigneeId !== assigneeFilter) return false;
      if (clientFilter !== 'All' && t.clientId !== clientFilter) return false;
      if (statusFilter === 'Open' && t.status === 'Done') return false;
      if (statusFilter === 'Done' && t.status !== 'Done') return false;
      if (statusFilter === 'Overdue') {
        const d = new Date(t.dueDate);
        if (t.status === 'Done') return false;
        if (d >= new Date(todayKey)) return false;
      }
      return true;
    });
  }, [taskList, categoryFilter, assigneeFilter, clientFilter, statusFilter, todayKey]);

  const byDay = useMemo(() => {
    const map = {};
    for (const t of filtered) {
      const k = dayKey(new Date(t.dueDate));
      (map[k] ||= []).push(t);
    }
    // Sort each day: overdue/open first, then done; then by title
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const doneA = a.status === 'Done' ? 1 : 0;
        const doneB = b.status === 'Done' ? 1 : 0;
        if (doneA !== doneB) return doneA - doneB;
        return (a.title || '').localeCompare(b.title || '');
      });
    }
    return map;
  }, [filtered]);

  // KPI strip — over the visible month only
  const monthStats = useMemo(() => {
    const monthNum = cursor.getMonth();
    const year = cursor.getFullYear();
    let due = 0, overdue = 0, done = 0;
    const today = new Date(todayKey);
    for (const t of filtered) {
      const d = new Date(t.dueDate);
      if (d.getFullYear() !== year || d.getMonth() !== monthNum) continue;
      due += 1;
      if (t.status === 'Done') done += 1;
      else if (d < today) overdue += 1;
    }
    return { due, overdue, done };
  }, [filtered, cursor, todayKey]);

  const gotoPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const gotoNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const gotoToday = () => { setCursor(startOfMonth(new Date())); setSelectedDay(new Date()); };

  const openAddTaskForDay = (d) => {
    setDialogDefaultDate(dayKey(d));
    setTaskDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading calendar...</div>
      </div>
    );
  }

  const selKey = selectedDay ? dayKey(selectedDay) : null;
  const selTasks = selKey ? (byDay[selKey] || []) : [];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Calendar"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2">
                <CalendarIcon size={24} className="text-emerald-800"/> Deadline calendar
              </h1>
              <p className="mt-1 text-stone-600 text-sm">Every SARS &amp; CIPC due date across your firm — at a glance.</p>
            </div>
            <div className="flex items-center gap-2" data-testid="calendar-nav">
              <Button variant="outline" size="icon" onClick={gotoPrev} data-testid="calendar-prev-btn"><ChevronLeft size={16}/></Button>
              <div className="min-w-[180px] text-center font-serif text-lg font-semibold text-stone-900" data-testid="calendar-month-label">{monthLabel}</div>
              <Button variant="outline" size="icon" onClick={gotoNext} data-testid="calendar-next-btn"><ChevronRight size={16}/></Button>
              <Button variant="outline" onClick={gotoToday} data-testid="calendar-today-btn"><CalendarDays size={14} className="mr-2"/>Today</Button>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="calendar-kpis">
            <div className="rounded-xl bg-white border border-stone-200 p-4">
              <div className="text-xs text-stone-500 font-medium">Due this month</div>
              <div className="mt-1 text-2xl font-serif font-semibold text-stone-900" data-testid="kpi-due">{monthStats.due}</div>
            </div>
            <div className="rounded-xl bg-white border border-stone-200 p-4">
              <div className="text-xs text-stone-500 font-medium flex items-center gap-1"><AlertTriangle size={12} className="text-red-600"/> Overdue</div>
              <div className="mt-1 text-2xl font-serif font-semibold text-red-700" data-testid="kpi-overdue">{monthStats.overdue}</div>
            </div>
            <div className="rounded-xl bg-white border border-stone-200 p-4">
              <div className="text-xs text-stone-500 font-medium flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600"/> Completed</div>
              <div className="mt-1 text-2xl font-serif font-semibold text-emerald-700" data-testid="kpi-completed">{monthStats.done}</div>
            </div>
            <div className="rounded-xl bg-white border border-stone-200 p-4">
              <div className="text-xs text-stone-500 font-medium">Showing</div>
              <div className="mt-1 text-2xl font-serif font-semibold text-stone-900" data-testid="kpi-showing">{filtered.length}<span className="text-sm text-stone-500 font-sans font-normal ml-1">tasks</span></div>
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl bg-white border border-stone-200 p-4 flex flex-wrap items-end gap-3" data-testid="calendar-filters">
            <div className="min-w-[160px]">
              <div className="text-xs text-stone-500 mb-1">Category</div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-9" data-testid="filter-category"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {TASK_CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <div className="text-xs text-stone-500 mb-1">Assignee</div>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="h-9" data-testid="filter-assignee"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All assignees</SelectItem>
                  {teamMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <div className="text-xs text-stone-500 mb-1">Client</div>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="h-9" data-testid="filter-client"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All clients</SelectItem>
                  {clientList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <div className="text-xs text-stone-500 mb-1">Status</div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="filter-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Overdue">Overdue</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-600">
              {['VAT', 'PAYE', 'CIPC', 'Taxation', 'Bookkeeping', 'Audit'].map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${CATEGORY_DOT[c]}`}/> {c}
                </span>
              ))}
            </div>
          </div>

          {/* Month grid + side panel */}
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="calendar-grid">
              <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
                {WEEK_LABELS.map((w) => (
                  <div key={w} className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-stone-500 text-center">{w}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 auto-rows-fr">
                {grid.map((d, idx) => {
                  const k = dayKey(d);
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const isToday = k === todayKey;
                  const isSelected = selKey === k;
                  const dayTasks = byDay[k] || [];
                  const hasOverdue = dayTasks.some((t) => t.status !== 'Done' && new Date(t.dueDate) < new Date(todayKey));
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedDay(d)}
                      data-testid={`day-cell-${k}`}
                      className={`text-left min-h-[110px] border-r border-b border-stone-100 last:border-r-0 px-2 pt-1.5 pb-2 transition-colors relative
                        ${inMonth ? 'bg-white hover:bg-stone-50' : 'bg-stone-50/60 hover:bg-stone-100 text-stone-400'}
                        ${isSelected ? 'ring-2 ring-emerald-600 ring-inset z-[1]' : ''}
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`inline-grid place-items-center h-6 w-6 rounded-full text-xs font-semibold
                          ${isToday ? 'bg-emerald-800 text-white' : inMonth ? 'text-stone-800' : 'text-stone-400'}`}>
                          {d.getDate()}
                        </span>
                        {hasOverdue && <AlertTriangle size={11} className="text-red-600"/>}
                      </div>
                      <div className="mt-1 space-y-1">
                        {dayTasks.slice(0, 3).map((t) => {
                          const cls = CATEGORY_BAR[t.category || 'Other'];
                          const done = t.status === 'Done';
                          const overdue = !done && new Date(t.dueDate) < new Date(todayKey);
                          return (
                            <div
                              key={t.id}
                              className={`truncate text-[11px] px-1.5 py-0.5 rounded-sm border-l-2 ${cls}
                                ${done ? 'line-through opacity-60' : ''}
                                ${overdue ? 'ring-1 ring-red-300' : ''}`}
                              title={`${t.title}${t.clientName ? ' · ' + t.clientName : ''}`}
                            >
                              {t.title}
                            </div>
                          );
                        })}
                        {dayTasks.length > 3 && (
                          <div className="text-[10px] text-stone-500 pl-1">+{dayTasks.length - 3} more</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Side panel */}
            <aside className="rounded-xl bg-white border border-stone-200 overflow-hidden self-start sticky top-20" data-testid="day-panel">
              {!selectedDay ? (
                <div className="p-6 text-center text-sm text-stone-500">
                  <CalendarIcon size={22} className="mx-auto text-stone-300 mb-2"/>
                  Click any day to see deadlines and add tasks.
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-stone-100 flex items-start justify-between">
                    <div>
                      <div className="text-xs text-stone-500">
                        {selectedDay.toLocaleDateString(undefined, { weekday: 'long' })}
                      </div>
                      <div className="font-serif text-lg font-semibold text-stone-900">
                        {selectedDay.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    </div>
                    <button onClick={() => setSelectedDay(null)} className="text-stone-400 hover:text-stone-700" data-testid="close-day-panel">
                      <X size={16}/>
                    </button>
                  </div>
                  <div className="p-4 space-y-3 max-h-[520px] overflow-y-auto">
                    {selTasks.length === 0 && (
                      <div className="text-sm text-stone-500 text-center py-6">
                        Nothing scheduled for this day.
                      </div>
                    )}
                    {selTasks.map((t) => {
                      const done = t.status === 'Done';
                      return (
                        <div key={t.id} className="rounded-lg border border-stone-200 p-3 hover:border-emerald-500 transition-colors" data-testid={`day-task-${t.id}`}>
                          <div className="flex items-start gap-2">
                            <button
                              onClick={() => changeStatus(t, done ? 'To Do' : 'Done')}
                              className="mt-0.5"
                              title={done ? 'Mark as open' : 'Mark as done'}
                              data-testid={`toggle-done-${t.id}`}
                            >
                              {done
                                ? <CheckCircle2 size={16} className="text-emerald-600"/>
                                : <Circle size={16} className="text-stone-300 hover:text-emerald-600"/>}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium text-sm text-stone-900 ${done ? 'line-through text-stone-500' : ''}`}>{t.title}</div>
                              <div className="text-xs text-stone-500 mt-0.5 truncate">
                                {t.clientName || 'No client'} · {t.assignee || 'Unassigned'}
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <CategoryChip category={t.category || 'Other'}/>
                                <Badge variant="outline" className="text-[10px] border-stone-300">{t.status}</Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-3 border-t border-stone-100">
                    <Button
                      onClick={() => openAddTaskForDay(selectedDay)}
                      className="w-full bg-emerald-800 hover:bg-emerald-900 text-white"
                      data-testid="add-task-for-day-btn"
                    >
                      <Plus size={14} className="mr-2"/> Add task for this day
                    </Button>
                  </div>
                </>
              )}
            </aside>
          </div>
        </main>
      </div>

      <NewTaskDialog
        open={taskDialogOpen}
        onOpenChange={(v) => { setTaskDialogOpen(v); if (!v) setDialogDefaultDate(''); }}
        clientsList={clientList}
        teamMembers={teamMembers}
        currentUserId={user?.id}
        defaultDate={dialogDefaultDate}
        onCreated={fetchAll}
      />
    </div>
  );
}
